import { isStagingTestMode, stagingEmailMessage } from './stagingEnvironment'
import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import { supabase } from './supabase'
const { workshopAccessLevel, readOnlyUntil } = require('./workshopAccess.js')

const AuthContext = createContext({})

const PROFILE_SELECT = `
  id, workshop_id, full_name, role, is_superadmin, created_at,
  workshops!profiles_workshop_id_fkey (
    id, name, email, phone, address, created_at, subscription_status,
    stripe_customer_id, stripe_subscription_id, subscription_start,
    subscription_end, slug, max_jobs_per_day, booking_notification_email,
    weekly_bookable_hours,
    archived_at, is_demo, demo_reset_at,
    invoice_prefix, invoice_counter, reminders_enabled, logo_url,
    discount_approval_required,
    billing_amount_pence, billing_currency, billing_day,
    billing_last_invoice_status, billing_last_payment_status,
    billing_next_payment_at
  )
`

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [workshop, setWorkshop] = useState(null)
  const [profile, setProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const userRef = useRef(null)
  const profileRef = useRef(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      const nextUser = session?.user ?? null
      userRef.current = nextUser
      setUser(nextUser)
      if (nextUser) fetchProfile(nextUser.id)
      else setLoading(false)
    })

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      const path = window.location.pathname
      const hash = window.location.hash || ''
      if (event === 'PASSWORD_RECOVERY' && path !== '/reset-password') {
        window.location.replace('/reset-password')
        return
      }
      if (hash.includes('type=invite') && path !== '/accept-invite') {
        window.location.replace(`/accept-invite${hash}`)
        return
      }

      const nextUser = session?.user ?? null
      const existingProfile = profileRef.current
      const isSameUser = Boolean(nextUser && (userRef.current?.id === nextUser.id || existingProfile?.id === nextUser.id))
      userRef.current = nextUser
      setUser(nextUser)
      if (nextUser) {
        // Must flip loading back to true before the async fetch starts —
        // otherwise PrivateRoute can render on this same tick with user set
        // but workshop still null/stale, evaluate isActive as false, and
        // redirect to /subscribe before fetchProfile's await even returns.
        // Keep existing screens mounted for silent auth refreshes so
        // in-progress form edits are not wiped when a tab is refocused.
        if (!isSameUser || !existingProfile) setLoading(true)
        fetchProfile(nextUser.id)
      } else {
        profileRef.current = null
        setWorkshop(null)
        setProfile(null)
        setLoading(false)
      }
    })

    return () => subscription.unsubscribe()
  }, [])

  async function fetchProfile(userId, isRetry = false) {
    const { data: profileData, error } = await supabase
      .from('profiles')
      .select(PROFILE_SELECT)
      .eq('id', userId)
      .maybeSingle()

    if (error) {
      console.error('fetchProfile error:', error.message, error)
      // One automatic retry — covers transient blips (cold start, brief
      // network failure) without permanently stranding the user on
      // /subscribe due to a single failed fetch with no visibility.
      if (!isRetry) {
        await new Promise(r => setTimeout(r, 800))
        return fetchProfile(userId, true)
      }
      setLoading(false)
      return
    }

    if (!profileData) {
      profileRef.current = null
      setProfile(null)
      setWorkshop(null)
    }
    if (profileData) {
      const workshopData = profileData.workshops
        ? { ...profileData.workshops, general_workshop_enabled: false }
        : null
      if (workshopData?.id) {
        const { data: moduleData, error: moduleError } = await supabase
          .from('workshops')
          .select('general_workshop_enabled')
          .eq('id', workshopData.id)
          .single()
        if (!moduleError) workshopData.general_workshop_enabled = moduleData?.general_workshop_enabled === true
        else if (!String(moduleError.message || '').includes('general_workshop_enabled')) {
          console.warn('general_workshop_enabled lookup failed:', moduleError.message)
        }
      }
      const nextProfile = { ...profileData, workshops: workshopData }
      profileRef.current = nextProfile
      setProfile(nextProfile)
      setWorkshop(workshopData)
    }
    setLoading(false)
  }

  async function refreshWorkshop() {
    if (!user) return
    await fetchProfile(user.id)
  }

  async function signIn(email, password) {
    setLoading(true)
    const { error } = await supabase.auth.signInWithPassword({ email, password })
    if (error) setLoading(false)
    return { error }
  }

  async function signUp(email, password, workshopName, fullName) {
    if (isStagingTestMode()) return { error: new Error(stagingEmailMessage) }
    const { error } = await supabase.auth.signUp({
      email, password,
      options: { emailRedirectTo: `${window.location.origin}/setup-workshop`, data: { workshop_name: workshopName, full_name: fullName } }
    })
    return { error }
  }

  async function signOut() {
    await supabase.auth.signOut()
  }

  const workshopId = workshop?.id ?? null
  const subscriptionStatus = workshop?.subscription_status ?? 'inactive'
  const isSuperAdmin = profile?.is_superadmin ?? false
  // Same levels the database and functions enforce (workshopAccess.js).
  const accessLevel = isSuperAdmin ? 'full' : workshopAccessLevel(workshop)
  const unfinishedSignup = ['pending', 'incomplete'].includes(String(subscriptionStatus).toLowerCase())
  // A read-only workshop can still open the app to view and export its records;
  // an unfinished signup goes to /subscribe instead.
  const isActive = accessLevel === 'full' || (accessLevel === 'read_only' && !unfinishedSignup)
  const canAccessApp = isActive || isSuperAdmin
  const accessEndsAt = accessLevel === 'read_only' ? readOnlyUntil(workshop) : null

  return (
    <AuthContext.Provider value={{
      user, workshop, profile, workshopId, loading,
      subscriptionStatus, isActive: canAccessApp, isSuperAdmin, accessLevel, accessEndsAt,
      signIn, signUp, signOut, refreshWorkshop
    }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext)
